/* eslint-disable max-lines */
import {
  useAddUser,
  useValidRoles,
  useTenants,
} from '@hakimo-ui/hakimo/data-access';
import {
  Alert,
  Button,
  InputField,
  Modal,
  Radio,
  Label,
} from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';
import { environment } from '@hakimo-ui/hakimo/environments';
import { isValidEmail, isValidPassword, validateInputs } from './util';
import { toast, useUser } from '@hakimo-ui/hakimo/util';
import { QueryResult, MultiSelect } from '@hakimo-ui/hakimo/ui-elements';
import { UserRole, Tenant } from '@hakimo-ui/hakimo/types';
interface Props {
  open: boolean;
  onClose: () => void;
}
export function AddUserModal(props: Props) {
  const { open, onClose } = props;
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [employeeId, setEmployeeId] = useState<string>('');
  const roleQuery = useValidRoles();
  const tenantsQuery = useTenants();
  const [role, setRole] = useState<UserRole | null>(null);
  const [password, setPassword] = useState<string>('');
  const loggedInUser = useUser();

  const allUserTenants = [
    loggedInUser.tenant,
    ...(loggedInUser.mspTenants || []),
  ].filter(
    (tenant, index, array) =>
      array.findIndex((t) => t.id === tenant.id) === index
  );

  const totalAvailableTenants = allUserTenants.length;
  const shouldHideTenantFields = totalAvailableTenants <= 1;

  const [tenants, setTenants] = useState<Tenant[]>(allUserTenants);
  const resetModal = () => {
    setName('');
    setEmail('');
    setEmployeeId('');
    setRole(null);
    setTenants(allUserTenants);
  };
  const addUserMutation = useAddUser(() => {
    resetModal();
    onClose();
    toast('User added', { type: 'success' });
  });
  const onCloseCb = () => {
    resetModal();
    onClose();
    addUserMutation.reset();
  };

  const actions = (
    <>
      <Button
        variant="primary"
        onClick={() => {
          const primaryTenant =
            tenants.length > 0 ? tenants[0] : loggedInUser.tenant;

          if (role && primaryTenant) {
            const allMspTenants = tenants.map((tenant) => tenant.id);

            addUserMutation.mutate({
              name: name,
              email: email,
              employee_id: employeeId || undefined,
              roles: [role],
              // only add password for local auth
              password: environment.authMode === 'local' ? password : undefined,
              tenant_id: primaryTenant.id,
              msp_tenants: allMspTenants.join(','),
              vision_tenants: allMspTenants.join(','),
            });
          }
        }}
        // Only enable if input is valid
        disabled={
          !validateInputs(
            name,
            email,
            role,
            password,
            environment.authMode === 'local',
            tenants.length > 0 ? tenants[0] : null
          )
        }
        loading={addUserMutation.isLoading}
      >
        Submit
      </Button>
      <Button onClick={onCloseCb}>Cancel</Button>
    </>
  );
  return (
    <Modal title="Add User" open={open} onClose={onCloseCb} footer={actions}>
      <div className="w-[40rem] max-w-6xl">
        {addUserMutation.isError ? (
          <div className="px-8 py-4">
            <Alert type="error">{addUserMutation.error.message} </Alert>
          </div>
        ) : null}
        <div className="mb-4 px-8 py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <InputField
              onChange={(e) => setName(e.target.value)}
              label="Name"
              type="text"
              value={name}
            />
            <InputField
              onChange={(e) => setEmail(e.target.value)}
              label="Email"
              type="email"
              error={!isValidEmail(email) && email.length > 0}
              errorText="Invalid email"
              value={email}
            />
            <InputField
              onChange={(e) => setEmployeeId(e.target.value)}
              label="Employee ID"
              type="text"
              value={employeeId}
            />
          </div>
        </div>
        {!shouldHideTenantFields && (
          <div className="mb-4 px-8 py-4">
            <QueryResult queryResult={tenantsQuery}>
              {(data) => (
                <div>
                  <label className="dark:text-dark-text mb-1 block text-xs text-gray-700">
                    Tenants
                  </label>
                  <div className="relative mb-2">
                    <MultiSelect
                      getItems={(query: string) =>
                        data.tenants.filter(
                          (t) =>
                            t.name
                              .toLowerCase()
                              .includes(query.toLowerCase()) &&
                            !tenants.some((selected) => selected.id === t.id)
                        )
                      }
                      displayValue={(item) => item?.name ?? ''}
                      onChange={setTenants}
                      value={tenants}
                      customSelectedRenderer={() => <div />}
                    />
                  </div>
                  {tenants.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {tenants.map((it, idx) => {
                        const isBaseTenant = it.id === loggedInUser.tenant.id;
                        return (
                          <span key={it.id}>
                            <Label
                              text={it.name}
                              removable={!isBaseTenant}
                              onClickRemove={() =>
                                !isBaseTenant &&
                                setTenants([
                                  ...tenants.slice(0, idx),
                                  ...tenants.slice(idx + 1),
                                ])
                              }
                            />
                          </span>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </QueryResult>
          </div>
        )}

        {environment.authMode === 'local' && (
          <div className="mb-4 px-8 py-4">
            <InputField
              onChange={(e) => setPassword(e.target.value)}
              label="Password"
              type="password"
              error={!isValidPassword(password)}
              errorText={
                'Password must be at least 14 ' +
                'characters long and contain one uppercase ' +
                'letter, one lowercase letter, one digit and ' +
                'one special character'
              }
              value={password}
            />
          </div>
        )}
        <div className="mb-2 px-8 py-4">
          <QueryResult queryResult={roleQuery}>
            {(data) => (
              <div>
                <div className="max-h-56 overflow-y-auto rounded-md border border-gray-600 p-2">
                  <Radio
                    items={data}
                    value={role}
                    onChange={setRole}
                    displayValue={(item) => item ?? ''}
                  />
                </div>
              </div>
            )}
          </QueryResult>
        </div>
      </div>
    </Modal>
  );
}

export default AddUserModal;
